import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Eye, EyeOff, Building2, User, Mail, Lock, Globe, MapPin, CheckCircle2, ArrowRight, AlertCircle, UserCheck } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useDataMode } from '../contexts/DataModeContext';
import { useDropdown, useCities, PhoneField } from '../contexts/DropdownContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const BENEFITS = [
  'Access hundreds of vetted stand contractors',
  'Submit a brief once, receive multiple quotes',
  'Track all your projects in one dashboard',
  'Get matched with contractors in your region',
  'Compare proposals side-by-side',
  'Dedicated account support',
];

const InputField = ({ label, icon: Icon, error, required, children, hint }) => (
  <div className="space-y-1.5">
    <label className="block text-sm font-medium text-gray-700">
      {label} {required && <span className="text-blue-500">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <Icon size={16} />
        </div>
      )}
      {children}
    </div>
    {hint && !error && <p className="text-xs text-gray-400">{hint}</p>}
    {error && (
      <p className="text-xs text-red-500 flex items-center gap-1">
        <AlertCircle size={11} /> {error}
      </p>
    )}
  </div>
);

const inputClass = (hasIcon, error) =>
  `w-full border rounded-lg px-3 py-2.5 text-sm transition-all outline-none focus:ring-2 ${
    error
      ? 'border-red-300 focus:ring-red-100 focus:border-red-400'
      : 'border-gray-200 focus:ring-blue-100 focus:border-blue-400'
  } ${hasIcon ? 'pl-9' : ''}`;

export default function ExhibitorSignup() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const { login } = useAuth();
  const { isMockMode, toggleMode } = useDataMode();
  const { countries } = useDropdown();
  const [orgCountry, setOrgCountry] = useState('');
  const [phoneField, setPhoneField] = useState({ code: '+90', number: '' });
  const { cities } = useCities(orgCountry);
  const [baseCurrency, setBaseCurrency] = useState('USD');

  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState('');
  const [inviteDetails, setInviteDetails] = useState(null);
  const [inviteError, setInviteError] = useState(null);

  // Fetch invite details if invite code is in URL
  useEffect(() => {
    if (!inviteCode) return;
    axios.get(`${BACKEND_URL}/api/auth/invite/${inviteCode}`)
      .then(res => {
        setInviteDetails(res.data);
        if (res.data.email) setForm(f => ({ ...f, email: res.data.email }));
      })
      .catch(() => setInviteError('Invalid or expired invite link. Please contact the team admin.'));
  }, [inviteCode]);

  const [form, setForm] = useState({
    company_name: '',
    country: '',
    city: '',
    website: '',
    name: '',
    email: '',
    password: '',
    confirm_password: '',
    accept_terms: false,
  });

  const set = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
    setErrors(prev => { const n = { ...prev }; delete n[field]; return n; });
    setServerError('');
  };

  const fillTestData = () => {
    setForm({
      company_name: 'Messe Frankfurt Exhibition GmbH',
      country: 'DE',
      city: 'Frankfurt',
      website: 'https://mf-exhibitions.de',
      name: 'Thomas Brenner',
      email: 'thomas.brenner@mf-exhibitions.de',
      password: '123456Ab&',
      confirm_password: '123456Ab&',
      accept_terms: true,
    });
    setOrgCountry('DE');
    setBaseCurrency('EUR');
    setPhoneField({ code: '+49', number: '17612345678' });
    setErrors({});
    setServerError('');
  };

  const validate = () => {
    const errs = {};
    if (!inviteCode && (!form.company_name.trim() || form.company_name.length < 2)) errs.company_name = 'Company name must be at least 2 characters';
    if (!inviteCode && !form.country) errs.country = 'Please select a country';
    if (!form.name.trim() || form.name.length < 2) errs.name = 'Full name must be at least 2 characters';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Please enter a valid email address';
    if (!phoneField.number || phoneField.number.length < 6) errs.phone = 'Please enter a valid phone number (min 6 digits)';
    if (!form.password || form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (form.password !== form.confirm_password) errs.confirm_password = 'Passwords do not match';
    if (!form.accept_terms) errs.accept_terms = 'You must accept the terms to continue';
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    setLoading(true);
    setServerError('');

    try {
    const payload = {
        role: 'EXHIBITOR',
        user: {
          name: form.name,
          email: form.email,
          phone_e164: phoneField.code + phoneField.number,
          password: form.password,
        },
        accept_terms: true,
        language_preference: localStorage.getItem('expostand_lang') || 'en',
        base_currency: baseCurrency,
      };

      if (inviteCode) {
        payload.invite_code = inviteCode;
      } else {
        payload.org = {
          display_name: form.company_name,
          country: form.country,
          city: form.city || undefined,
          website: form.website || undefined,
        };
      }

      await axios.post(`${BACKEND_URL}/api/auth/signup`, payload);

      // Auto-login after successful signup
      const result = await login(form.email, form.password);
      if (result.success) {
        // Ensure new users see their real data, not mock data
        if (isMockMode) toggleMode();
        // New exhibitor → onboarding wizard (unless already completed)
        const onboardingDone = localStorage.getItem('exhibitor_onboarding_done');
        navigate(onboardingDone ? '/app/exhibitor/dashboard' : '/app/exhibitor/onboarding');
      } else {
        // Login failed for some reason, redirect to login page
        navigate('/exhibitor/login', { state: { email: form.email, message: 'Account created! Please log in.' } });
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        setServerError(detail);
      } else if (detail?.message) {
        setServerError(detail.message);
      } else if (detail?.code === 'EMAIL_EXISTS') {
        setErrors(prev => ({ ...prev, email: 'This email is already registered' }));
      } else {
        setServerError('Registration failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-950 flex">

      {/* ── Sol Panel ── */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-blue-900 via-blue-800 to-sky-900 flex-col justify-between p-12 overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:'radial-gradient(circle at 30% 40%, #fff 1px, transparent 1px), radial-gradient(circle at 70% 60%, #fff 1px, transparent 1px)', backgroundSize:'40px 40px'}} />
        <div className="relative">
          <Link to="/" className="flex items-center gap-2 text-white font-black text-xl tracking-tight">
            <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
              <span className="text-blue-700 font-black text-sm">ES</span>
            </div>
            <span>EXPO STAND</span>
          </Link>
        </div>

        <div className="relative space-y-8">
          <div>
            <span className="text-blue-300 text-xs font-bold uppercase tracking-widest">Exhibitor Portalı</span>
            <h1 className="text-4xl font-black text-white mt-2 leading-tight">
              Sergini<br /><span className="text-blue-300">Mükemmel Yönet</span>
            </h1>
            <p className="text-blue-200 mt-4 text-sm leading-relaxed">
              Stand sektöründe doğrulanmış yüklenicilerle buluşun. Tek brief, çoklu teklif — zaman kaybı yok.
            </p>
          </div>
          <div className="space-y-3">
            {BENEFITS.map((b, i) => (
              <div key={i} className="flex items-center gap-3">
                <div className="w-5 h-5 rounded-full bg-blue-700 flex items-center justify-center shrink-0">
                  <CheckCircle2 className="w-3 h-3 text-blue-200" />
                </div>
                <span className="text-blue-100 text-sm">{b}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="relative">
          <p className="text-blue-300 text-xs">Zaten hesabın var mı?{' '}
            <Link to="/exhibitor/login" className="text-white font-semibold hover:underline">Giriş yap →</Link>
          </p>
        </div>
      </div>

      {/* ── Sağ Panel — Form ── */}
      <div className="w-full lg:w-1/2 flex items-start justify-center p-6 sm:p-10 overflow-y-auto bg-white">
        <div className="w-full max-w-md pt-8">
          <div className="mb-8">
            <div className="w-12 h-12 rounded-2xl bg-blue-600 flex items-center justify-center mb-4">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h2 className="text-2xl font-black text-gray-900">Exhibitor Hesabı Oluştur</h2>
                <p className="text-gray-500 text-sm mt-1">Ücretsiz kaydol, yüklenici bul</p>
              </div>
              <button
                type="button"
                onClick={fillTestData}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-blue-500/50 text-blue-400 text-xs font-semibold hover:bg-blue-900/30 transition-colors"
                data-testid="fill-test-data-btn"
              >
                ⚡ Fill Test Data
              </button>
            </div>
          </div>

          {serverError && (
            <div data-testid="signup-error" className="mb-6 p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              {serverError}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate className="space-y-4">

            {/* Invite Banner */}
            {inviteCode && (
              <div className={`rounded-xl p-4 border ${inviteError ? 'bg-red-900/30 border-red-800' : 'bg-blue-900/30 border-blue-700'}`}>
                {inviteError ? (
                  <div className="flex items-center gap-2 text-sm text-red-300">
                    <AlertCircle size={16} className="shrink-0" />
                    {inviteError}
                  </div>
                ) : inviteDetails ? (
                  <div className="flex items-start gap-3">
                    <UserCheck size={18} className="text-blue-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-blue-100">Davet aldınız!</p>
                      <p className="text-xs text-blue-300 mt-0.5">
                        <span className="font-semibold">{inviteDetails.org_name}</span> ekibine katılıyorsunuz
                      </p>
                    </div>
                  </div>
                ) : (
                  <p className="text-xs text-blue-400">Davet detayları yükleniyor...</p>
                )}
              </div>
            )}

            {/* Company Section */}
            {!inviteCode && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-1">
                  <div className="w-5 h-5 rounded-full bg-blue-700 flex items-center justify-center">
                    <Building2 className="w-3 h-3 text-blue-200" />
                  </div>
                  <span className="text-blue-600 text-xs font-bold uppercase tracking-widest">Şirket Bilgileri</span>
                </div>

                <div>
                  <input
                    data-testid="input-company-name"
                    type="text"
                    value={form.company_name}
                    onChange={e => set('company_name', e.target.value)}
                    placeholder="Şirket Adı *"
                    className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors placeholder-gray-400"
                  />
                  {errors.company_name && <p className="text-red-400 text-xs mt-1">{errors.company_name}</p>}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <select
                      data-testid="select-country"
                      value={form.country}
                      onChange={e => { set('country', e.target.value); setOrgCountry(e.target.value); set('city', ''); }}
                      className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                    >
                      <option value="">Ülke *</option>
                      {countries.length > 0
                        ? countries.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)
                        : [{code:'TR',name:'Türkiye',flag:'🇹🇷'},{code:'AE',name:'UAE',flag:'🇦🇪'},{code:'US',name:'USA',flag:'🇺🇸'}].map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)
                      }
                    </select>
                    {errors.country && <p className="text-red-400 text-xs mt-1">{errors.country}</p>}
                  </div>
                  <div>
                    {cities.length > 0 ? (
                      <select
                        data-testid="input-city"
                        value={form.city}
                        onChange={e => set('city', e.target.value)}
                        className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors appearance-none"
                      >
                        <option value="">Şehir</option>
                        {cities.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                      </select>
                    ) : (
                      <input
                        data-testid="input-city"
                        type="text"
                        value={form.city}
                        onChange={e => set('city', e.target.value)}
                        placeholder="Şehir"
                        className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors placeholder-gray-400"
                      />
                    )}
                  </div>
                </div>

                <input
                  data-testid="input-website"
                  type="text"
                  value={form.website}
                  onChange={e => set('website', e.target.value)}
                  placeholder="Şirket web sitesi (opsiyonel)"
                  className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors placeholder-gray-400"
                />

                <div>
                  <p className="text-gray-600 text-xs mb-2">Para Birimi *</p>
                  <div className="grid grid-cols-5 gap-2">
                    {[
                      { code: 'USD', symbol: '$' },
                      { code: 'EUR', symbol: '€' },
                      { code: 'GBP', symbol: '£' },
                      { code: 'TRY', symbol: '₺' },
                      { code: 'AED', symbol: 'د.إ' },
                    ].map(c => (
                      <button
                        key={c.code}
                        type="button"
                        data-testid={`currency-${c.code}`}
                        onClick={() => setBaseCurrency(c.code)}
                        className={`flex flex-col items-center justify-center p-2 rounded-xl border-2 text-xs font-semibold transition-all ${
                          baseCurrency === c.code
                            ? 'border-blue-500 bg-blue-50 text-blue-700'
                            : 'border-gray-200 text-gray-500 hover:border-blue-400'
                        }`}
                      >
                        <span className="text-base leading-none mb-0.5">{c.symbol}</span>
                        <span>{c.code}</span>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-gray-800" />

                <div className="flex items-center gap-2 pb-1">
                  <div className="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center">
                    <User className="w-3 h-3 text-gray-300" />
                  </div>
                  <span className="text-gray-500 text-xs font-bold uppercase tracking-widest">Kişisel Bilgiler</span>
                </div>
              </div>
            )}

            {/* Personal */}
            <div className="space-y-3">
              <div>
                <input
                  data-testid="input-name"
                  type="text"
                  value={form.name}
                  onChange={e => set('name', e.target.value)}
                  placeholder="Ad Soyad *"
                  className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors placeholder-gray-400"
                />
                {errors.name && <p className="text-red-400 text-xs mt-1">{errors.name}</p>}
              </div>

              <div>
                <input
                  data-testid="input-email"
                  type="email"
                  value={form.email}
                  onChange={e => set('email', e.target.value)}
                  placeholder="E-posta adresi *"
                  className="w-full bg-white border border-gray-200 text-gray-900 text-sm rounded-xl px-4 py-3 focus:outline-none focus:border-blue-500 transition-colors placeholder-gray-400"
                />
                {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
              </div>

              <div data-testid="phone-field-wrapper">
                <PhoneField value={phoneField} onChange={setPhoneField} label="" required />
                {errors.phone && <p className="text-red-400 text-xs mt-1">{errors.phone}</p>}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="relative">
                  <input
                    data-testid="input-password"
                    type={showPassword ? 'text' : 'password'}
                    value={form.password}
                    onChange={e => set('password', e.target.value)}
                    placeholder="Şifre *"
                    className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-xl px-4 py-3 pr-10 focus:outline-none focus:border-blue-500 transition-colors placeholder-gray-500"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showPassword ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
                </div>
                <div className="relative">
                  <input
                    data-testid="input-confirm-password"
                    type={showConfirm ? 'text' : 'password'}
                    value={form.confirm_password}
                    onChange={e => set('confirm_password', e.target.value)}
                    placeholder="Şifre tekrar *"
                    className="w-full bg-gray-800 border border-gray-700 text-white text-sm rounded-xl px-4 py-3 pr-10 focus:outline-none focus:border-blue-500 transition-colors placeholder-gray-500"
                  />
                  <button type="button" onClick={() => setShowConfirm(!showConfirm)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500">
                    {showConfirm ? <EyeOff size={15} /> : <Eye size={15} />}
                  </button>
                  {errors.confirm_password && <p className="text-red-400 text-xs mt-1">{errors.confirm_password}</p>}
                </div>
              </div>
            </div>

            {/* Terms */}
            <label className="flex items-start gap-3 cursor-pointer">
              <div className="relative mt-0.5 shrink-0">
                <input
                  data-testid="checkbox-terms"
                  type="checkbox"
                  checked={form.accept_terms}
                  onChange={e => set('accept_terms', e.target.checked)}
                  className="sr-only"
                />
                <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                  form.accept_terms ? 'bg-blue-600 border-blue-600' : 'border-gray-600'
                }`}>
                  {form.accept_terms && (
                    <svg viewBox="0 0 10 8" className="w-3 h-2.5 text-white fill-none stroke-current stroke-2">
                      <path d="M1 4l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-gray-600 leading-relaxed">
                <a href="#" className="text-blue-400 hover:underline">Kullanım Şartları</a> ve{' '}
                <a href="#" className="text-blue-400 hover:underline">Gizlilik Politikası</a>'nı kabul ediyorum.
              </span>
            </label>
            {errors.accept_terms && <p className="text-red-400 text-xs">{errors.accept_terms}</p>}

            {/* Submit */}
            <button
              data-testid="btn-create-account"
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white font-bold py-3.5 rounded-xl flex items-center justify-center gap-2 transition-all text-sm"
            >
              {loading ? (
                <>
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Hesap oluşturuluyor...
                </>
              ) : (
                <>Hesap Oluştur <ArrowRight size={16} /></>
              )}
            </button>
          </form>

          <p className="text-center text-xs text-gray-400 mt-6">
            Hesap oluşturarak şartlarımızı kabul etmiş olursunuz. Verileriniz GDPR kapsamında korunur.
          </p>
        </div>
      </div>
    </div>
  );
}
